import { useLayoutEffect, useState } from "react";

import {
  DECISION_FORBIDDEN,
  ENVIRONMENT_MISMATCH_IS_SANDBOX,
  SingleDecisionEnvelope,
} from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { DecisionEnvironment, DecisionSummaryResponse } from "src/api/types";
import { HistoricalDecisionApiResponse } from "src/clients/history_v3";
import { useDecisionEnvSearchParam } from "src/decisionsOverview/useDecisionEnvSearchParam";

export const useDecisionEnv = (
  canReadLive: boolean,
  canReadSandbox: boolean,
) => {
  const [rawEnvParam, setDecisionEnvParam] = useDecisionEnvSearchParam();
  const [confirmedNoLiveDecisions, setConfirmedNoLiveDecisions] =
    useState(false);
  const canRead = (env: DecisionEnvironment) =>
    env === DecisionEnvironment.LIVE ? canReadLive : canReadSandbox;
  const decisionEnvParam =
    rawEnvParam !== null && !canRead(rawEnvParam)
      ? rawEnvParam === DecisionEnvironment.LIVE
        ? DecisionEnvironment.SANDBOX
        : DecisionEnvironment.LIVE
      : rawEnvParam;
  const decisionEnvForQueries =
    decisionEnvParam ??
    (canReadLive && (!confirmedNoLiveDecisions || !canReadSandbox)
      ? DecisionEnvironment.LIVE
      : DecisionEnvironment.SANDBOX);
  return {
    decisionEnvParam,
    decisionEnvForQueries,
    confirmedNoLiveDecisions,
    setDecisionEnvParam,
    setConfirmedNoLiveDecisions,
  };
};

/**
 * If there is no environment URL param set we determine the environment based on the query results.
 */
export const useDetermineDecisionEnvironment = ({
  decisionEnvParam,
  entityId,
  decisionsOverview,
  setDecisionEnvParam,
  decisionId,
  singleDecision,
  confirmedNoLiveDecisions,
  setConfirmedNoLiveDecisions,
  canReadLive,
  decisionEnvForQueries,
}: {
  canReadLive: boolean;
  decisionEnvForQueries: DecisionEnvironment;
  decisionEnvParam: DecisionEnvironment | null;
  entityId: string | null;
  decisionId: string | null;
  decisionsOverview: DecisionSummaryResponse | undefined;
  singleDecision:
    | SingleDecisionEnvelope<HistoricalDecisionApiResponse>
    | undefined;
  confirmedNoLiveDecisions: boolean;
  setConfirmedNoLiveDecisions: (value: boolean) => void;
  setDecisionEnvParam: (env: DecisionEnvironment) => void;
}) => {
  const preferredEnvironment = canReadLive
    ? DecisionEnvironment.LIVE
    : DecisionEnvironment.SANDBOX;

  // Layout effect to pre-empt the query results getting rendered in case we want to switch the environment.
  useLayoutEffect(() => {
    if (decisionEnvParam !== null) {
      if (confirmedNoLiveDecisions) {
        setConfirmedNoLiveDecisions(false);
      }
      return;
    }

    if (entityId !== null && decisionsOverview !== undefined) {
      if (!confirmedNoLiveDecisions) {
        if (decisionsOverview.groups.length === 0) {
          setConfirmedNoLiveDecisions(true);
        } else {
          setDecisionEnvParam(preferredEnvironment);
        }
      }
      if (confirmedNoLiveDecisions) {
        if (decisionsOverview.groups.length === 0) {
          setDecisionEnvParam(preferredEnvironment);
        } else {
          setDecisionEnvParam(DecisionEnvironment.SANDBOX);
        }
      }
    }
    if (decisionId !== null && singleDecision !== undefined) {
      if (
        singleDecision?.isQueryError &&
        singleDecision.reason === ENVIRONMENT_MISMATCH_IS_SANDBOX
      ) {
        setDecisionEnvParam(DecisionEnvironment.SANDBOX);
      } else if (
        singleDecision?.isQueryError &&
        singleDecision.reason === DECISION_FORBIDDEN
      ) {
        // A refusal carries no decision, so it says nothing about the
        // environment. Pin the one already queried: it resolves the toggle
        // without asking for the refused decision again.
        setDecisionEnvParam(decisionEnvForQueries);
      } else {
        setDecisionEnvParam(preferredEnvironment);
      }
    }
  }, [
    decisionEnvParam,
    entityId,
    decisionsOverview,
    setDecisionEnvParam,
    decisionId,
    singleDecision,
    decisionEnvForQueries,
    confirmedNoLiveDecisions,
    setConfirmedNoLiveDecisions,
    preferredEnvironment,
  ]);
};
